import React, { useState, useEffect } from "react";
import {
  Box,
  CssBaseline,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  Tooltip,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { useNavigate } from "react-router-dom";

// Components
import Sidebar, { drawerWidth as DRAWER_WIDTH, collapsedWidth as COLLAPSED_WIDTH } from "../components/Sidebar";
import PurchaseInvoiceForm from "../components/PurchaseInvoiceForm";
import SaleInvoiceForm from "../components/SaleInvoiceForm";
import ACCOUNTS from "../components/Accounts";
import PRODUCTS from "../components/Products";
import PURCHASEREPORTS from "../components/PurchaseReports";
import BookingNumber from "../components/BookingNumber";
import SaleOrders from "../components/SaleOrders";
import PurchaseOrders from "../components/PurchaseOrders";
import Commission from "../components/Commission";
import ContainerForm from "../components/ContainerForm";
import PickupScheduled from "../components/PickupScheduled";
import CompanyDocuments from "../components/CompanyDocuments";
import Freight from "../components/Freight";
import Dashboard from "../components/Dashboard";
import RegradeMaterials from "../components/RegradeMaterials";
import PackingList from "../components/PackingList";
import FinishedGoods from "../components/FinishedGoods";
import WireChopping from "../components/WireChopping";
import WireLibrary from "../components/WireLibrary";
import WireCompare from "../components/WireCompare";
import PriceDashboard from "../components/PriceDashboard";
import PendingIntakes from "../components/PendingIntakes";
import DailyCash from "../components/DailyCash";
import GeneralLedger from "../components/GeneralLedger";
import EmployeeLoans from "../components/EmployeeLoans";
import TruckingInvoices from "../components/TruckingInvoices";
import ChartOfAccounts from "../components/ChartOfAccounts";
import JournalEntry from "../components/JournalEntry";
import ProfitLoss from "../components/ProfitLoss";
import BalanceSheet from "../components/BalanceSheet";
import TrialBalance from "../components/TrialBalance";
import InventoryValuation from "../components/InventoryValuation";
import InventoryAging from "../components/InventoryAging";
import RecoveryAnalytics from "../components/RecoveryAnalytics";
import PriceAlerts from "../components/PriceAlerts";
import ContainerProfit from "../components/ContainerProfit";
import ProductPriceMap from "../components/ProductPriceMap";
import GlometsPriceList from "../components/GlometsPriceList";
import AgingReport from "../components/AgingReport";
import AccountLedger from "../components/AccountLedger";
import CashFlow from "../components/CashFlow";
import ComparativeProfitLoss from "../components/ComparativeProfitLoss";
import CustomerStatement from "../components/CustomerStatement";
import BankReconciliation from "../components/BankReconciliation";
import QuickBooks from "../components/QuickBooks";
import ActiveSessions from "../components/ActiveSessions";
import EmployeePanel from "../components/EmployeePanel";
import UserManagement from "../components/UserManagement";
import MappingManager from "../components/MappingManager";
import PendingInvoices from "../components/PendingInvoices";
import BrokerPayments from "../components/BrokerPayments";
import FreightReport from "../components/FreightReport";
import CompaniesAdmin from "../components/CompaniesAdmin";

import { logout, setToken } from "../services/auth";
import { canSee } from "../config/permissions";
import api from "../services/api";

const SIDEBAR_COLLAPSED_KEY = "sidebar_collapsed";

export default function MainPage() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeContent, setActiveContent] = useState("Dashboard");
  // Persisted desktop mini-drawer preference. Loaded from localStorage so the
  // user's choice survives reloads + carries between sessions.
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1"; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? "1" : "0"); }
    catch { /* ignore */ }
  }, [collapsed]);
  // Ctrl+B (or Cmd+B on Mac) toggles the sidebar — industry-standard shortcut
  // that VS Code, Slack, Discord, etc. use.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "b") {
        e.preventDefault();
        setCollapsed((c) => !c);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const sidebarW = collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH;
  const [role, setRole] = useState(() => {
    try { return JSON.parse(localStorage.getItem("user") || "{}").role || "viewer"; }
    catch { return "viewer"; }
  });
  const [modules, setModules] = useState(() => {
    try {
      const m = JSON.parse(localStorage.getItem("user") || "{}").modules;
      return Array.isArray(m) ? m : null;
    } catch { return null; }
  });
  const [isPlatformOwner, setIsPlatformOwner] = useState(() => {
    try { return !!JSON.parse(localStorage.getItem("user") || "{}").isPlatformOwner; } catch { return false; }
  });
  const [companyName, setCompanyName] = useState(() => {
    try { return JSON.parse(localStorage.getItem("user") || "{}").companyName || ""; } catch { return ""; }
  });
  // Sidebar "Needs Pricing" badge counts (recent unpriced invoices).
  const [needsPricing, setNeedsPricing] = useState({});
  React.useEffect(() => {
    api.get("/invoicePayments/needsPricingCount")
      .then((r) => setNeedsPricing(r.data || {}))
      .catch(() => {});
  }, [activeContent]);

  // When a "Needs Pricing" row is clicked, open that invoice in its editor.
  const [pricingTarget, setPricingTarget] = useState(null); // { type, id }
  const openForPricing = (type, id) => {
    setPricingTarget({ type, id });
    setActiveContent(type === "Sale" ? "SALE INVOICE" : "PURCHASE INVOICE");
  };
  const navigate = useNavigate();

  // Hydrate role + module list from /auth/me on every load so admin
  // permission changes take effect on the next page reload (no need to log
  // out / back in to pick up new access).
  React.useEffect(() => {
    api.get("/auth/me")
      .then((r) => {
        if (r.data?.token) setToken(r.data.token); // sliding refresh — keeps session alive
        const u = r.data?.user || {};
        const newRole = u.role || "viewer";
        const newModules = Array.isArray(u.modules) ? u.modules : null;
        setRole(newRole);
        setModules(newModules);
        setIsPlatformOwner(!!u.isPlatformOwner);
        setCompanyName(u.companyName || "");
        const cached = JSON.parse(localStorage.getItem("user") || "{}");
        localStorage.setItem("user", JSON.stringify({
          ...cached, role: newRole, modules: newModules,
          companyId: u.companyId ?? null, companyName: u.companyName ?? null,
          isPlatformOwner: !!u.isPlatformOwner,
        }));
      })
      .catch(() => { /* ignore — token may be expired */ });
  }, []);

  // Auto-pick a landing module the user can actually see. If they can't see
  // Dashboard, drop them on the first sidebar item they DO have access to.
  // Without this, viewers without 'dashboard' permission land on a blank page.
  React.useEffect(() => {
    // The platform owner has its own landing logic (next effect) and navigates
    // among platform-only screens (COMPANIES/USERS/AUDIT LOGS) that aren't
    // modules — so this module-based picker must not run for them, or it would
    // bounce them off AUDIT LOGS back to the first "accessible" label.
    if (isPlatformOwner) return;
    // Admins are bound by the company module list too (it's already capped for
    // them server-side); only skip this when there is no list at all.
    if (!Array.isArray(modules)) return;
    if (modules.length === 0) return;
    const gate = modules;
    // If current activeContent is already accessible, keep it
    if (canSee(gate, activeContent)) return;
    // Find the first menu label the user can see
    const allLabels = [
      "Dashboard",
      "EMPLOYEE PANEL",
      "PENDING INTAKES", "PURCHASE INVOICE", "PURCHASE INVOICE PENDING", "BROKER PAYMENTS", "PURCHASE ORDERS", "COMMISSIONS",
      "SALE INVOICE", "SALE INVOICE PENDING", "SALE ORDERS",
      "BOOKING #", "CONTAINER FORM", "PICKUP SCHEDULED", "COMPANY DOCUMENTS", "FREIGHT LANE",
      "ACCOUNTS", "EMPLOYEE LOANS", "TRUCKING INVOICES",
      "PRODUCTS", "DAILY CASH", "REGRADE MATERIALS",
      "FINISHED GOODS", "WIRE CHOPPING", "RECOVERY ANALYTICS", "PACKING LIST", "WIRE LIBRARY", "IDENTIFY WIRE",
      "STANDARD REPORTS", "FREIGHT REPORTS", "GENERAL LEDGER", "CHART OF ACCOUNTS", "JOURNAL ENTRY", "PROFIT & LOSS", "BALANCE SHEET", "INVENTORY AGING", "CONTAINER PROFIT", "QUICKBOOKS", "PRICE DASHBOARD", "PRICE ALERTS", "PRICE MAPPINGS", "MARKET PRICE LINK",
      "USERS",
    ];
    const first = allLabels.find((lbl) => canSee(gate, lbl));
    if (first) setActiveContent(first);
  }, [modules, role, activeContent, isPlatformOwner]);

  // Platform owner lands on Companies (they have no operational dashboard).
  React.useEffect(() => {
    if (isPlatformOwner && !["COMPANIES", "USERS", "AUDIT LOGS"].includes(activeContent)) {
      setActiveContent("COMPANIES");
    }
  }, [isPlatformOwner]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleLogout = () => logout(navigate);

  const contentMap = {
    Dashboard: <Dashboard onNavigate={setActiveContent} />,
    ACCOUNTS: <ACCOUNTS />,
    PRODUCTS: <PRODUCTS />,
    "PURCHASE INVOICE": <PurchaseInvoiceForm openId={pricingTarget?.type === "Purchase" ? pricingTarget.id : null} onConsumed={() => setPricingTarget(null)} />,
    "SALE INVOICE": <SaleInvoiceForm openId={pricingTarget?.type === "Sale" ? pricingTarget.id : null} onConsumed={() => setPricingTarget(null)} />,
    "DAILY CASH": <DailyCash />,
    "REGRADE MATERIALS": <RegradeMaterials />,
    "PACKING LIST": <PackingList />,
    "FINISHED GOODS": <FinishedGoods />,
    "WIRE CHOPPING": <WireChopping />,
    "WIRE LIBRARY": <WireLibrary />,
    "IDENTIFY WIRE": <WireCompare />,
    "STANDARD REPORTS": <PURCHASEREPORTS />,
    "PRICE DASHBOARD": <PriceDashboard />,
    "GLOMETS PRICE LIST": <GlometsPriceList />,
    "PENDING INTAKES": <PendingIntakes />,
    "GENERAL LEDGER": <GeneralLedger />,
    "EMPLOYEE LOANS": <EmployeeLoans />,
    "TRUCKING INVOICES": <TruckingInvoices />,
    "CHART OF ACCOUNTS": <ChartOfAccounts />,
    "JOURNAL ENTRY": <JournalEntry />,
    "PROFIT & LOSS": <ProfitLoss />,
    "BALANCE SHEET": <BalanceSheet />,
    "TRIAL BALANCE": <TrialBalance />,
    "INVENTORY VALUATION": <InventoryValuation />,
    "INVENTORY AGING": <InventoryAging />,
    "CONTAINER PROFIT": <ContainerProfit />,
    "RECOVERY ANALYTICS": <RecoveryAnalytics />,
    "PRICE ALERTS": <PriceAlerts />,
    "AR / AP AGING": <AgingReport />,
    "GL ACCOUNT DETAIL": <AccountLedger />,
    "CASH FLOW": <CashFlow />,
    "COMPARATIVE P&L": <ComparativeProfitLoss />,
    "CUSTOMER STATEMENT": <CustomerStatement />,
    "BANK RECONCILIATION": <BankReconciliation />,
    "QUICKBOOKS": <QuickBooks />,
    "BOOKING #": <BookingNumber />,
    "SALE ORDERS": <SaleOrders />,
    "PURCHASE ORDERS": <PurchaseOrders />,
    COMMISSIONS: <Commission />,
    "CONTAINER FORM": <ContainerForm />,
    "PICKUP SCHEDULED": <PickupScheduled />,
    "COMPANY DOCUMENTS": <CompanyDocuments />,
    "FREIGHT LANE": <Freight />,
    USERS: <UserManagement />,
    "PRICE MAPPINGS": <MappingManager />,
    "MARKET PRICE LINK": <ProductPriceMap />,
    "PURCHASE INVOICE PENDING": <PendingInvoices invoiceType="Purchase" onOpenInvoice={(id) => openForPricing("Purchase", id)} />,
    "SALE INVOICE PENDING":     <PendingInvoices invoiceType="Sale" onOpenInvoice={(id) => openForPricing("Sale", id)} />,
    "BROKER PAYMENTS":          <BrokerPayments />,
    "FREIGHT REPORTS":          <FreightReport />,
    COMPANIES:                  <CompaniesAdmin />,
    "AUDIT LOGS":               <ActiveSessions />,
    "EMPLOYEE PANEL":           <EmployeePanel />,
  };

  // Guard the rendered section: even if someone forces a route by accident,
  // the effective module list gates what they see. Admin role always passes.
  const renderContent = () => {
    // The platform owner only ever sees platform management — never a company's
    // operational screens.
    if (isPlatformOwner) {
      if (activeContent === "USERS") return contentMap.USERS;
      if (activeContent === "AUDIT LOGS") return contentMap["AUDIT LOGS"];
      return contentMap.COMPANIES;
    }
    // COMPANIES is platform-owner-only; nobody else may open it.
    if (activeContent === "COMPANIES") {
      return <Typography>Not authorized.</Typography>;
    }
    const gate = modules ?? role;
    // Admin bypasses the gate only when there's no module list; with a list,
    // the company cap applies to admins too.
    const adminBypass = role === "admin" && !Array.isArray(modules);
    if (!adminBypass && !canSee(gate, activeContent)) {
      // Special case: zero modules at all = user has no access
      if (Array.isArray(modules) && modules.length === 0) {
        return (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="h6" sx={{ mb: 1 }}>No modules assigned</Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              You don't have access to any modules yet. Please contact your
              administrator to assign permissions.
            </Typography>
          </Box>
        );
      }
      return <Typography>You don't have access to "{activeContent}".</Typography>;
    }
    return contentMap[activeContent] || <Typography>Section not found</Typography>;
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <CssBaseline />

      {/* Header */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${sidebarW}px)` },
          ml: { md: `${sidebarW}px` },
          zIndex: (theme) => theme.zIndex.drawer + 1,
          transition: (theme) => theme.transitions.create(["width", "margin"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.shorter,
          }),
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 }, px: { xs: 1, sm: 2 } }}>
          {/* Mobile: open drawer */}
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 1, display: { md: "none" }, width: 48, height: 48 }}
            aria-label="open menu"
          >
            <MenuIcon />
          </IconButton>

          {/* Desktop: collapse / expand sidebar */}
          <Tooltip title={collapsed ? "Expand sidebar (Ctrl+B)" : "Collapse sidebar (Ctrl+B)"}>
            <IconButton
              color="inherit"
              edge="start"
              onClick={() => setCollapsed((c) => !c)}
              sx={{ mr: 1, display: { xs: "none", md: "inline-flex" } }}
              aria-label="toggle sidebar"
            >
              {collapsed ? <MenuOpenIcon /> : <ChevronLeftIcon />}
            </IconButton>
          </Tooltip>

          <Typography
            variant="h6"
            noWrap
            sx={{
              flexGrow: 1,
              fontSize: { xs: "1rem", sm: "1.25rem" },
              minWidth: 0,
            }}
          >
            {activeContent}
          </Typography>

          {(companyName || isPlatformOwner) && (
            <Typography variant="body2" noWrap sx={{ mr: 2, opacity: 0.85, display: { xs: "none", sm: "block" } }}>
              {isPlatformOwner ? "Platform Owner" : companyName}
            </Typography>
          )}
          <Button color="inherit" onClick={handleLogout} size="small">
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      {/* Sidebar — pass effective module list when available, else role for backwards compat */}
      <Sidebar
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
        onMenuClick={setActiveContent}
        activeContent={activeContent}
        role={role}
        modules={modules}
        isPlatformOwner={isPlatformOwner}
        companyName={companyName}
        collapsed={collapsed}
        badges={{
          "PURCHASE INVOICE PENDING": needsPricing.Purchase || 0,
          "SALE INVOICE PENDING": needsPricing.Sale || 0,
        }}
      />

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0, // critical: lets the content shrink and prevents horizontal overflow
          width: { xs: "100%", md: `calc(100% - ${sidebarW}px)` },
          p: { xs: 1, sm: 2, md: 3 },
          pt: { xs: 9, sm: 10, md: 11 }, // space for fixed AppBar
          maxWidth: "100%",
          overflowX: "hidden",
          transition: (theme) => theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.shorter,
          }),
        }}
      >
        {renderContent()}
      </Box>
    </Box>
  );
}
